import bwipjs from 'bwip-js';
import { isURL } from 'class-validator';
import { optimize } from 'svgo';

import { PixelData, SvgData } from '../types';
import { getPrefixTransformStyleString } from './css';
import { PrinterContext } from './printer';

export const lineBreak = () => {
  return '<br>';
};

export const wrapInDiv = (htmlString: string, styles: Record<string, string> = {}) => {
  let styleString = '';
  for (const [tag, value] of Object.entries(styles)) {
    styleString += `${tag}: ${value};`;
  }

  return `<div style="${styleString}">${htmlString}</div>`;
};

// Styling based on https://github.com/receipt-print-hq/escpos-tools/blob/master/src/resources/esc2html.css
export const text = (data: string, printerContext: PrinterContext) => {
  let inlineStyleString = 'display: inline-block;';

  for (const [style, value] of Object.entries(printerContext.getTextState())) {
    if (typeof value === 'boolean' && value) {
      switch (style) {
        case 'bold':
          inlineStyleString += 'font-weight: bold;';
          break;

        case 'italic':
          inlineStyleString += 'font-style: italic;';
          break;

        case 'underline':
          inlineStyleString += 'text-decoration: underline;';
          break;

        default:
          break;
      }
    }
  }

  const [width, height] = [printerContext.getTextState().width, printerContext.getTextState().height];
  const isScaled = width !== 1 || height !== 1;
  if (isScaled) {
    inlineStyleString += getPrefixTransformStyleString(`scale(${width}, ${height})`);

    if (height !== 1) {
      inlineStyleString += `margin-bottom: ${height - 1}em;`;
    }
  }

  switch (printerContext.getTextState().position) {
    case 'center':
      inlineStyleString += 'width: 100%;';
      inlineStyleString += 'text-align: center;';
      if (isScaled) inlineStyleString += 'transform-origin: 50% 0;';
      break;

    case 'right':
      inlineStyleString += 'width: 100%;';
      inlineStyleString += 'text-align: right;';
      if (isScaled) inlineStyleString += 'transform-origin: 100% 0;';
      break;

    case 'left':
      if (isScaled) inlineStyleString += 'transform-origin: 0;';
      break;
  }

  const dummyDiv = document.createElement('div');
  dummyDiv.innerHTML = `<span style="${inlineStyleString}"></span>`;
  dummyDiv.querySelector('span').textContent = data;

  return dummyDiv.querySelector('span').outerHTML;
};

export const twoDimensionalCodeImage = (type: 'barcode' | 'pdf417' | 'qrcode', printerContext: PrinterContext) => {
  if (printerContext.getCodeBuffer().length === 0) {
    console.log('[Warning]: codeBuffer is empty while printing command is executed');
    return '';
  }

  let codeSVGElement = '';
  const [code] = printerContext.getCodeBuffer().slice(-1);

  switch (type) {
    case 'barcode': {
      const currentBarcodeState = printerContext.getBarcodeState();
      const barcodeRenderingOptions: any = {
        bcid: currentBarcodeState.type,
        text: code.join(''),

        /**
         * Multiply the height based on the n-dots configuration of the printer (in mm)
         * which is 0.150 mm
         *
         * Note: Its actually different for each printer but here we will just take
         *       the biggest possible value
         */
        height: currentBarcodeState.height * 0.15,

        textxalign: 'center',
        textcolor: '000000',
      };

      if (currentBarcodeState.printPosition !== 'nope') {
        barcodeRenderingOptions.includetext = true;
        barcodeRenderingOptions.textsize = 5;

        if (currentBarcodeState.printPosition === 'top') {
          barcodeRenderingOptions.textyalign = 'above';
        } else {
          // This is the case for "both" and "below" since bwip-js
          // doesnt support both text rendering position
          barcodeRenderingOptions.textyalign = 'below';
        }
      } else {
        barcodeRenderingOptions.includetext = false;
      }

      codeSVGElement = `${lineBreak()}${(bwipjs.toSVG(barcodeRenderingOptions) as string).trimEnd()}${lineBreak()}`;
      break;
    }

    case 'pdf417': {
      const currentPDF417State = printerContext.getPdf417State();

      // Convert each byte into its codePoint and use
      // the ^NNN format to represent these codePoints
      const formattedCode = [];
      for (const byte of code) {
        formattedCode.push(`^${parseInt(byte, 16).toString().padStart(3, '0')}`);
      }

      const pdf417RenderingOptions: any = {
        bcid: currentPDF417State.type === 'standard' ? 'pdf417' : 'pdf417compact',
        text: formattedCode.join(''),
        height: currentPDF417State.height,
        widht: currentPDF417State.width,
        textxalign: 'center',
        textcolor: '000000',

        // Since bwip-js only support up to maximum error correction level of 5
        eclevel: Math.min(currentPDF417State.errorCorrectionLevel, 5),
      };

      if (currentPDF417State.rowSize !== 0) {
        pdf417RenderingOptions.rows = currentPDF417State.rowSize;
      }

      if (currentPDF417State.colSize !== 0) {
        pdf417RenderingOptions.columns = currentPDF417State.colSize;
      }

      if (currentPDF417State.errorCorrectionLevel === 0) {
        pdf417RenderingOptions.eclevel = 1;
      }

      codeSVGElement = `${lineBreak()}${(bwipjs.toSVG(pdf417RenderingOptions) as string).trimEnd()}${lineBreak()}`;
      break;
    }

    case 'qrcode': {
      const currentQRCodeState = printerContext.getQRCodeState();

      // Convert each byte into its codePoint and use
      // the ^NNN format to represent these codePoints
      const formattedCode = code.map((byte) => `^${parseInt(byte, 16).toString().padStart(3, '0')}`);

      const qrcodeRenderingOptions: any = {
        bcid: currentQRCodeState.model === 'micro' ? 'microqrcode' : 'qrcode',
        text: formattedCode.join(''),
        textxalign: 'center',
        textcolor: '000000',
        eclevel: currentQRCodeState.errorCorrectionLevel,

        // Just an option to enable auto unicode decoding
        parse: 'true',
      };

      if (currentQRCodeState.model !== 'micro') {
        qrcodeRenderingOptions.version = Number(currentQRCodeState.model);
      }

      // TODO: Scale using the svg property and render it via optimize function call
      let svgElement = (bwipjs.toSVG(qrcodeRenderingOptions) as string).trimEnd();

      svgElement = (() => {
        // Add anchor to open up a link if the code has any
        const codeAsString = code.map((byte) => String.fromCodePoint(parseInt(byte, 16))).join('');

        let extractedURL = '';
        if (isURL(codeAsString)) {
          extractedURL = codeAsString;
        }

        if (extractedURL === '') {
          return svgElement;
        } else {
          try {
            new URL(extractedURL);
          } catch (e) {
            return svgElement;
          }
        }

        const dummySvgElement = document.createElement('div');
        dummySvgElement.innerHTML = svgElement;

        const dummyAnchorElement = document.createElement('div');
        dummyAnchorElement.innerHTML = `
          <a style="cursor: pointer"
             target="_blank" href="${extractedURL}"
             xlink:href="${extractedURL}"
             xlink:show="new"
             rel="noopener" rel="noreferrer">
            <rect x="0" y="0" width="100%" height="100%" fill-opacity="0"/>
          </a>
        `;

        const dummySvg = dummySvgElement.querySelector('svg');
        dummySvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        dummySvg.appendChild(dummyAnchorElement.querySelector('a'));

        return dummySvg.outerHTML;
      })();

      codeSVGElement = `${lineBreak()}${svgElement}${lineBreak()}`;
      break;
    }

    default:
      break;
  }

  return codeSVGElement;
};

// prettier-ignore
export const svgImage = (pixels: PixelData[][], printerContext: PrinterContext) => {
  const data = imageToSVGData(pixels);

  let styleTag = "";
  for (const [tag, value] of Object.entries(data.styles)) {
    styleTag += `${tag}="${value}" `;
  }

  let styleString = "";
  switch (printerContext.getTextState().position) {
    case 'center':
      styleString += 'margin-right: auto;'
      // falls through
    case 'right':
      styleString += 'margin-left: auto;'
      break;
  }

  let svgElement = `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges" class="output" style="${styleString}" ${styleTag}>`;
     svgElement += `  <path fill="none" stroke="#000000" d="${data.svgData}" />`;
     svgElement += `</svg>`;

  return optimize(svgElement).data;
};

export const svgChunkImage = (pixels: PixelData[][]) => {
  return imageToSVGData(pixels);
};

// prettier-ignore
export const compileSvgChunkImage = (chunkImages: SvgData[], printerContext: PrinterContext) => {
  const imageFullWidth = String(Number(chunkImages[0].styles.width) * (chunkImages.length + 1));

  chunkImages[0].styles.viewBox = `0 -0.5 ${chunkImages[0].styles.height} ${imageFullWidth}`;
  chunkImages[0].styles.height = chunkImages[0].styles.width;

  // Set width as fit content
  delete chunkImages[0].styles.width;

  let styleTag = ` `;
  for (const [tag, value] of Object.entries(chunkImages[0].styles)) {
    styleTag += `${tag}="${value}" `;
  }

  let styleString = ``;

  const transformScale = chunkImages.length - 1;
  styleString += getPrefixTransformStyleString(`scale(${transformScale}) translateY(10px)`);

  switch (printerContext.getTextState().position) {
    case 'center':
      styleString += 'margin-right: auto;'
      // falls through
    case 'right':
      styleString += 'margin-left: auto;'
      break;
  }

  let svgElement = `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges" class="output" style="${styleString}" ${styleTag}>`;
  for (const [index, chunk] of chunkImages.entries()) {
    const translation = index * Number(chunk.styles.width);
    svgElement  += `  <path fill="none" stroke="#000000" transform="translate(${translation})" d="${chunk.svgData}" />`;
  }
    svgElement  += `</svg>`;

  // Optimize to combine multiple paths and do 
  // transformation directly on the path data
  svgElement = optimize(svgElement).data;

  /**
   * Here we flip the image horizontally and rotate it by 90 degree
   * due to the image extraction mentioned in graphics.ts
   */
  svgElement = optimize(svgElement.replace("<path", "<path transform=\"scale(1, -1) rotate(-90)\"")).data;
  return svgElement;
};

// Implementation based on
// https://stackoverflow.com/questions/73235335/how-to-create-image-from-a-2d-array-of-1s-and-0s-in-javascript
const imageToSVGData = (pixels: PixelData[][]): SvgData => {
  const pixelWidth = pixels.reduce((max, row) => Math.max(max, row.length), 0);
  const pixelHeight = pixels.length;

  const styles: Record<string, string> = {};
  // The `viewBox` of a SVG tag is the viewport to draw to.
  // Since we want 1 "pixel" in our vector to be 1 pixel in the output,
  // the viewbox will be 0 -0.5 4 3 for our 4x3 sample image.
  // The -0.5 shift upwards is because of how SVG aligns things.
  styles.viewBox = `0 -0.5 ${pixelWidth} ${pixelHeight}`;

  // The width/height of a SVG tag can be overwritten by CSS to your liking,
  // but having them makes sure the image stays the correct aspect ratio.
  styles.width = String(pixelWidth);
  styles.height = String(pixelHeight);

  let data = '';
  for (let line = 0; line < pixels.length; line++) {
    // [M]ove absolutely to 0,Y
    data += ` M0,${line}`;

    const row = pixels[line];
    for (const pixel of row) {
      if (pixel.r === 255 && pixel.g === 255 && pixel.b === 255) {
        // Draw a [h]orizontal line, 1 unit wide.
        data += ` h1`;
      } else {
        // [m]ove relatively to +1,+0.
        data += ` m1,0`;
      }
    }
  }

  return {
    styles,
    svgData: data,
  };
};
